import { useState } from 'react';
import { Loader2, DollarSign } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { extractErrorMessage } from '@/lib/error';
import { formatMoney } from '@/lib/money';
import { PageHeader } from '@/components/ui/PageHeader';
import { EmptyState } from '@/components/ui/EmptyState';
import {
  useShiftSummary,
  useCashDrops,
  useRecordCashDrop,
} from '@/hooks/api/useShift';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';
import type { ShiftMethodRow, CashDrop } from '@/services/api/frontdeskOps.types';

// ─── Tabs ────────────────────────────────────────────────────────────────────

type TabKey = 'summary' | 'cash-drop' | 'cash-drop-details';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'summary', label: 'Summary' },
  { key: 'cash-drop', label: 'Cash Drop' },
  { key: 'cash-drop-details', label: 'Cash Drop Details' },
];

// ─── Page ────────────────────────────────────────────────────────────────────

export function ShiftManagementPage() {
  const propertyId = useSelectedPropertyId();
  const [date, setDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [activeTab, setActiveTab] = useState<TabKey>('summary');

  const summaryQuery = useShiftSummary({ propertyId, date }, !!propertyId);
  const summary = summaryQuery.data;

  return (
    <div>
      <PageHeader title="Shift Management" description="Payment summary for a shift" />

      {/* Tab strip */}
      <div className="border-b border-gray-200 mb-6" role="tablist" aria-label="Shift management views">
        <div className="flex gap-0 -mb-px">
          {TABS.map((tab) => {
            const selected = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => setActiveTab(tab.key)}
                className={cn(
                  'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors',
                  selected
                    ? 'border-primary-600 text-primary-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                )}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Filter row — User filter hidden (FE-Q3): backend ignores userId in Phase 1. */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="flex items-center gap-2">
          <label htmlFor="shift-date" className="text-sm text-gray-600">Date</label>
          <input
            id="shift-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            aria-label="Shift date"
          />
        </div>
      </div>

      {/* Result region */}
      {!propertyId ? (
        <EmptyState icon={DollarSign} title="Select a property" description="Select a property to continue." />
      ) : activeTab === 'summary' ? (
        summaryQuery.isLoading ? (
          <LoadingSpinner />
        ) : summaryQuery.isError ? (
          <ErrorBanner message={extractErrorMessage(summaryQuery.error)} />
        ) : (
          <SummaryView totals={summary?.totals} byMethod={summary?.byMethod ?? []} />
        )
      ) : activeTab === 'cash-drop' ? (
        <CashDropView propertyId={propertyId} date={date} />
      ) : (
        <CashDropDetailsView propertyId={propertyId} date={date} summaryCash={summary?.totals?.cash} />
      )}
    </div>
  );
}

// ─── Shared bits ─────────────────────────────────────────────────────────────

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-20">
      <Loader2 size={32} className="animate-spin text-primary-500" aria-label="Loading" />
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
      {message}
    </p>
  );
}

/** Consistent money formatting across the page. */
function money(value: string | number | null | undefined): string {
  return formatMoney((Number(value) || 0).toFixed(2));
}

// ─── Summary view ────────────────────────────────────────────────────────────

function SummaryView({
  totals,
  byMethod,
}: {
  totals:
    | {
        collected: number;
        cash: number;
        check: number;
        cardOther: number;
        refunds: number;
        paidouts: number;
        net: number;
        cashDropped: number;
        cashInDrawer: number;
      }
    | undefined;
  byMethod: ShiftMethodRow[];
}) {
  const t = totals ?? {
    collected: 0,
    cash: 0,
    check: 0,
    cardOther: 0,
    refunds: 0,
    paidouts: 0,
    net: 0,
    cashDropped: 0,
    cashInDrawer: 0,
  };

  const tableTotal = byMethod.reduce((sum, r) => sum + (r.amount || 0), 0);

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <SummaryCard label="Total Collected" value={formatMoney(t.collected)} />
        <SummaryCard label="Cash" value={formatMoney(t.cash)} />
        <SummaryCard label="Check" value={formatMoney(t.check)} />
        <SummaryCard label="Card / Other" value={formatMoney(t.cardOther)} />
        <SummaryCard label="Refunds" value={formatMoney(t.refunds)} valueClass="text-red-600" />
        <SummaryCard label="Paidouts" value={formatMoney(t.paidouts)} />
        <SummaryCard label="Cash Dropped" value={formatMoney(t.cashDropped)} />
        <SummaryCard label="Cash in Drawer" value={formatMoney(t.cashInDrawer)} />
        <SummaryCard label="Net" value={formatMoney(t.net)} valueClass="text-green-700" />
      </div>

      {/* Breakdown table */}
      {byMethod.length === 0 ? (
        <EmptyState icon={DollarSign} title="No payments for this shift" description="No payments were recorded for the selected date." />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <Th>Method</Th>
                  <Th>Count</Th>
                  <Th>Amount</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {byMethod.map((r) => (
                  <tr key={r.method} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900 font-medium capitalize">{r.method}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{r.count}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{formatMoney(r.amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 font-semibold">
                  <td className="px-4 py-3 text-sm text-gray-700">Total</td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {byMethod.reduce((sum, r) => sum + (r.count || 0), 0)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">{formatMoney(tableTotal)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Cash Drop form view ─────────────────────────────────────────────────────

function CashDropView({ propertyId, date }: { propertyId: string; date: string }) {
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const recordMutation = useRecordCashDrop();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setSuccess(null);

    const parsed = Number(amount);
    if (!amount.trim() || !Number.isFinite(parsed) || parsed <= 0) {
      setFormError('Enter an amount greater than 0.');
      return;
    }

    recordMutation.mutate(
      {
        propertyId,
        date,
        amount: parsed,
        ...(note.trim() ? { note: note.trim() } : {}),
      },
      {
        onSuccess: () => {
          setSuccess(`Recorded a cash drop of ${money(parsed)}.`);
          setAmount('');
          setNote('');
        },
      },
    );
  };

  return (
    <div className="max-w-md">
      <form onSubmit={handleSubmit} className="space-y-4 bg-white rounded-xl border border-gray-200 p-5">
        <div>
          <label htmlFor="cd-amount" className="block text-sm font-medium text-gray-700 mb-1">
            Amount <span className="text-red-500">*</span>
          </label>
          <input
            id="cd-amount"
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            required
          />
        </div>

        <div>
          <label htmlFor="cd-note" className="block text-sm font-medium text-gray-700 mb-1">
            Note <span className="text-gray-400">(optional)</span>
          </label>
          <textarea
            id="cd-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder="e.g. Mid-shift drop to safe"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        {formError && <ErrorBanner message={formError} />}
        {recordMutation.isError && (
          <ErrorBanner message={extractErrorMessage(recordMutation.error)} />
        )}
        {success && (
          <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2" role="status">
            {success}
          </p>
        )}

        <button
          type="submit"
          disabled={recordMutation.isPending}
          className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {recordMutation.isPending && <Loader2 size={16} className="animate-spin" />}
          Record cash drop
        </button>
      </form>
    </div>
  );
}

// ─── Cash Drop Details view ──────────────────────────────────────────────────

function CashDropDetailsView({
  propertyId,
  date,
  summaryCash,
}: {
  propertyId: string;
  date: string;
  summaryCash: number | undefined;
}) {
  const dropsQuery = useCashDrops({ propertyId, date });
  const data = dropsQuery.data;

  if (dropsQuery.isLoading) return <LoadingSpinner />;
  if (dropsQuery.isError) return <ErrorBanner message={extractErrorMessage(dropsQuery.error)} />;

  const drops = data?.drops ?? [];
  const totalDropped = data?.totalDropped ?? 0;
  const cashInDrawer = (Number(summaryCash) || 0) - (Number(totalDropped) || 0);

  return (
    <div className="space-y-6">
      {/* Reconciliation cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <SummaryCard label="Cash Collected" value={money(summaryCash)} />
        <SummaryCard label="Total Dropped" value={money(totalDropped)} />
        <SummaryCard label="Cash in Drawer" value={money(cashInDrawer)} valueClass="text-green-700" />
      </div>

      {drops.length === 0 ? (
        <EmptyState icon={DollarSign} title="No cash drops for this shift" description="No cash drops were recorded for the selected date." />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <Th>Time</Th>
                  <Th>Amount</Th>
                  <Th>Note</Th>
                  <Th>Recorded by</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {drops.map((d) => (
                  <CashDropRow key={d.id} drop={d} />
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 font-semibold">
                  <td className="px-4 py-3 text-sm text-gray-700">Total dropped</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{money(totalDropped)}</td>
                  <td className="px-4 py-3" colSpan={2} />
                </tr>
                <tr className="bg-gray-50 font-semibold">
                  <td className="px-4 py-3 text-sm text-gray-700">Cash in drawer</td>
                  <td className="px-4 py-3 text-sm text-green-700">{money(cashInDrawer)}</td>
                  <td className="px-4 py-3 text-xs text-gray-500" colSpan={2}>
                    cash collected − total dropped
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function CashDropRow({ drop }: { drop: CashDrop }) {
  let time = '—';
  try {
    time = format(new Date(drop.createdAt), 'p');
  } catch {
    time = drop.createdAt;
  }
  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3 text-sm text-gray-700">{time}</td>
      <td className="px-4 py-3 text-sm text-gray-900 font-medium">{money(drop.amount)}</td>
      <td className="px-4 py-3 text-sm text-gray-700">{drop.note || <span className="text-gray-400">—</span>}</td>
      <td className="px-4 py-3 text-sm text-gray-700">{drop.recordedByName || drop.userId}</td>
    </tr>
  );
}

// ─── Small building blocks ───────────────────────────────────────────────────

function SummaryCard({
  label,
  value,
  valueClass,
}: {
  label: string;
  value: string;
  valueClass?: string;
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4" aria-label={`${label}: ${value}`}>
      <p className={cn('text-2xl font-bold text-gray-900', valueClass)}>{value}</p>
      <p className="text-sm text-gray-500 mt-1">{label}</p>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
      {children}
    </th>
  );
}
